import * as layout from '../markLayout.module.css';
import {
    MarkButton,
    MarkChoice,
    MarkChoiceLabel,
    MarkConfirmActions,
    MarkInput,
    MarkLabel
} from '../MarkControls';
import { flushSync } from 'react-dom';
import React, { useCallback, useContext, useId, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import P from '@hero/ui-kit/typography/P';
import type {
    TemplateTargetKeys,
    TemplateContent,
    Channel,
    MarkTemplateRow,
    MarkTemplateParent,
    TemplateLoadState,
    Block,
    BlockDoc,
    RenderBlockDoc
} from '../../types';
import useTemplateEditorContext from '../../api/useTemplateEditorContext';
import useTemplateValidate from '../../api/useTemplateValidate';
import useCreateMarkTemplate from '../../api/useCreateMarkTemplate';
import useUpdateMarkTemplate from '../../api/useUpdateMarkTemplate';
import { templateKeys, blastKeys, dripKeys } from '../../api/queryKeys';
import useUnsavedChangesRegistration from '../../hooks/useUnsavedChangesRegistration';
import { EditorContext } from './EditorContext';
import type { InsertTarget } from './EditorContext';
import EditableBlockList from './EditableBlockList';
import PreviewPanel, { PreviewHandle } from './PreviewPanel';
import ValidateBanner from './ValidateBanner';
import VariableList from './VariableList';
import TemplateWorkspaceLayout from './TemplateWorkspaceLayout';
import { useInsertTarget } from './useInsertTarget';
import useDebouncedValue from './useDebouncedValue';
import { describeErrorCode, NESTING_LIMIT } from './constants';
import { findContentErrors, textFieldError } from './contentChoices';
import PersonalizationButton from './PersonalizationButton';
import * as canvasStyles from './canvas.module.css';
import HtmlTemplateEditor from '../HtmlTemplateEditor';
import * as authoring from '../templateAuthoring.module.css';
import * as metadataStyles from '../htmlTemplateEditor.module.css';
import TemplateLibraryPicker, { useAsyncLifetime } from '../TemplateLibraryPicker';
import MarkModal from '../MarkModal';
import { UnsavedChangesContext } from '../UnsavedChangesProvider';
const VALIDATE_DEBOUNCE_MS = 400;
type Props = {
    markTemplate: TemplateContent | null;
    templateLoadState: TemplateLoadState;
    templateId: number | null;
    initialDirty?: boolean;
    readOnly?: boolean;
    workspace?: boolean;
    onSaved?: (id: number, row: MarkTemplateRow) => void;
} & (
    | { library: true; target?: never; channel: 'email'; targetKind?: never }
    | { library?: false; target: TemplateTargetKeys; channel: Channel; targetKind?: string | null }
);
type TemplateTextFieldProps = {
    id: string;
    label: string;
    value: string;
    disabled: boolean;
    onChange: (value: string) => void;
    error?: string;
    compact?: boolean;
};
const TemplateTextField: React.FC<TemplateTextFieldProps> = ({
    id,
    label,
    value,
    disabled,
    onChange,
    error,
    compact = false
}) => {
    const { registerField } = useInsertTarget();
    return (
        <div className={layout.field}>
            <div className={canvasStyles.metadataFieldHeader}>
                <MarkLabel htmlFor={id}>{label}</MarkLabel>
                <PersonalizationButton
                    compact={compact}
                    fieldId={id}
                    disabled={disabled}
                    onInsert={onChange}
                />
            </div>
            <MarkInput
                compact={compact}
                id={id}
                type="text"
                value={value}
                disabled={disabled}
                aria-invalid={!!error}
                aria-describedby={error ? `${id}-error` : undefined}
                onChange={(event) => onChange(event.currentTarget.value)}
                onFocus={registerField(id, onChange)}
                onBlur={registerField(id, onChange)}
            />
            {error && (
                <p id={`${id}-error`} className={layout.fieldError}>
                    {error}
                </p>
            )}
        </div>
    );
};
const BlockTemplateEditorShell: React.FC<Props & { guardToken: string }> = ({
    target,
    channel,
    markTemplate,
    templateLoadState,
    templateId,
    readOnly = false,
    targetKind,
    workspace = false,
    onSaved,
    initialDirty = false,
    library = false,
    guardToken
}) => {
    const canvasMode = workspace || library;
    const [blocks, setBlocks] = useState<Block[]>(markTemplate?.blocks ?? []);
    const [name, setName] = useState(markTemplate?.name ?? '');
    const [nameDirty, setNameDirty] = useState(initialDirty);
    const [subject, setSubject] = useState(markTemplate?.subject ?? '');
    const [preheader, setPreheader] = useState(markTemplate?.preheader ?? '');
    const [blocksDirty, setBlocksDirty] = useState(initialDirty);
    const [subjectDirty, setSubjectDirty] = useState(initialDirty);
    const [preheaderDirty, setPreheaderDirty] = useState(initialDirty);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [effectiveTemplateId, setEffectiveTemplateId] = useState(templateId);
    const [effectiveLoadState, setEffectiveLoadState] = useState(templateLoadState);
    const [insertTarget, setInsertTarget] = useState<InsertTarget | null>(null);
    const [saveAttempted, setSaveAttempted] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(
        !markTemplate?.subject?.trim() || (library && !markTemplate?.name?.trim())
    );
    const contentRef = useRef<HTMLFieldSetElement>(null);
    const previewRef = useRef<PreviewHandle>(null);
    const editorId = useId();
    const subjectId = `${editorId}-subject`;
    const preheaderId = `${editorId}-preheader`;
    const lifetime = useAsyncLifetime();
    const saveSequence = useRef(0);
    const onSavedRef = useRef(onSaved);
    onSavedRef.current = onSaved;
    const nameId = `${editorId}-name`;
    const nameError = library && !name.trim() ? 'Name is required.' : undefined;
    const contextQuery = useTemplateEditorContext(target ?? {});
    const contentValidation = useMemo(
        () => ({
            variables: contextQuery.data?.data?.variables,
            urlSlugs: contextQuery.data?.data?.url_slugs,
            targetKind
        }),
        [contextQuery.data, targetKind]
    );
    const subjectError =
        library && !subject.trim()
            ? 'A subject is required.'
            : channel === 'sms'
              ? undefined
              : textFieldError(subject, contentValidation);
    const preheaderError =
        channel === 'sms' ? undefined : textFieldError(preheader, contentValidation);
    const queryClient = useQueryClient();
    const createMutation = useCreateMarkTemplate();
    const updateMutation = useUpdateMarkTemplate();
    const blockDoc: BlockDoc = useMemo(() => ({ version: 1, blocks }), [blocks]);
    const renderDoc: RenderBlockDoc = useMemo(
        () => ({ ...blockDoc, preheader: preheader || undefined }),
        [blockDoc, preheader]
    );
    const liveValidateBody = useMemo(
        () => ({
            block_doc: renderDoc,
            subject,
            target_kind: targetKind ?? undefined,
            ...(library ? { library: true } : {})
        }),
        [renderDoc, subject, targetKind, library]
    );
    const validateBody = useDebouncedValue(liveValidateBody, VALIDATE_DEBOUNCE_MS);
    const docHash = useMemo(() => JSON.stringify(validateBody.block_doc), [validateBody]);
    const localErrors = useMemo(
        () => [
            ...(nameError ? [`Name: ${nameError}`] : []),
            ...(subjectError ? [`Subject: ${subjectError}`] : []),
            ...(preheaderError ? [`Preview text: ${preheaderError}`] : []),
            ...findContentErrors(
                blocks,
                contentValidation,
                channel === 'sms' ? 'Message content' : 'Email content'
            )
        ],
        [blocks, contentValidation, nameError, subjectError, preheaderError, channel]
    );
    const targetKey = guardToken;
    const isDirty = blocksDirty || subjectDirty || preheaderDirty || (library && nameDirty);
    const canSave = isDirty && !saving;
    const handleBlocksChange = useCallback((updatedBlocks: Block[]) => {
        setBlocks(updatedBlocks);
        setBlocksDirty(true);
    }, []);
    const buildParentKey = (): MarkTemplateParent => {
        if (!target) return { library: true };
        if (target.target_type === 'blast_variant') {
            return { blast_id: target.blast_id };
        }
        // Include both pinned version lists and the canvas's latest-draft list.
        return { drip_id: target.drip_id };
    };
    const buildTargetParams = () => {
        if (!target) return {};
        if (target.target_type === 'blast_variant') {
            return {
                target_type: target.target_type as string,
                blast_id: target.blast_id,
                variant_label: target.variant_label
            };
        }
        return {
            target_type: target.target_type as string,
            drip_id: target.drip_id,
            step_id: target.step_id,
            ...(target.version_number != null ? { version_number: target.version_number } : {})
        };
    };
    const handleSave = async () => {
        if (!isDirty || saving) return;
        const epoch = lifetime.current;
        const request = ++saveSequence.current;
        const current = () => lifetime.current === epoch && saveSequence.current === request;
        setSaveAttempted(true);
        if (localErrors.length > 0) {
            setPreviewOpen(false);
            if (nameError || subjectError || preheaderError) flushSync(() => setSettingsOpen(true));
            if (nameError) {
                document.getElementById(nameId)?.focus();
                return;
            }
            if (subjectError || preheaderError) {
                requestAnimationFrame(() =>
                    document.getElementById(subjectError ? subjectId : preheaderId)?.focus()
                );
                return;
            }
            const focusError = (remaining: number) =>
                requestAnimationFrame(() => {
                    const field = Array.from(
                        contentRef.current?.querySelectorAll<HTMLElement>(
                            'input[aria-invalid="true"], textarea[aria-invalid="true"], select[aria-invalid="true"]'
                        ) ?? []
                    ).find((element) => !element.closest('[hidden]'));
                    if (field) {
                        field.focus();
                        return;
                    }
                    const edit = Array.from(
                        contentRef.current?.querySelectorAll<HTMLButtonElement>(
                            '[data-content-invalid="true"] [data-edit-section][aria-expanded="false"]'
                        ) ?? []
                    ).find((element) => !element.closest('[hidden]'));
                    if (edit && remaining > 0) {
                        edit.click();
                        focusError(remaining - 1);
                    }
                });
            focusError(NESTING_LIMIT + 2);
            return;
        }
        setInsertTarget(null);
        const savedBlocksDirty = blocksDirty;
        const savedSubjectDirty = subjectDirty;
        const savedPreheaderDirty = preheaderDirty;
        const wasCreate = effectiveLoadState === 'missing';
        setSaving(true);
        setSaveError(null);
        try {
            let savedId = effectiveTemplateId;
            let savedRow: MarkTemplateRow;
            if (effectiveLoadState === 'missing') {
                const res = await createMutation.mutateAsync({
                    ...buildTargetParams(),
                    ...(library ? { name } : {}),
                    format: 'blocks',
                    channel,
                    subject,
                    preheader,
                    blocks
                });
                savedRow = res.data;
                savedId = res.data.id;
                if (current()) {
                    setEffectiveTemplateId(savedId);
                    setEffectiveLoadState('existing');
                }
            } else {
                const res = await updateMutation.mutateAsync({
                    id: effectiveTemplateId!,
                    format: 'blocks',
                    ...(library && nameDirty ? { name } : {}),
                    ...(savedSubjectDirty ? { subject } : {}),
                    ...(savedPreheaderDirty ? { preheader } : {}),
                    ...(savedBlocksDirty ? { blocks } : {})
                });
                savedRow = res.data;
            }
            if (current()) {
                if (savedBlocksDirty) setBlocksDirty(false);
                if (savedSubjectDirty) setSubjectDirty(false);
                if (savedPreheaderDirty) setPreheaderDirty(false);
                setNameDirty(false);
            }
            await queryClient.invalidateQueries({
                queryKey: library ? templateKeys.all : templateKeys.rows(buildParentKey())
            });
            if (target?.target_type === 'blast_variant') {
                await queryClient.invalidateQueries({
                    queryKey: blastKeys.detail(target.blast_id)
                });
                await queryClient.invalidateQueries({
                    queryKey: blastKeys.validate(target.blast_id)
                });
            } else if (target?.target_type === 'drip_step') {
                await queryClient.invalidateQueries({
                    queryKey: dripKeys.detail(target.drip_id, target.version_number)
                });
                await queryClient.invalidateQueries({
                    queryKey: dripKeys.validate(target.drip_id)
                });
            }
            if (savedId != null) {
                await queryClient.invalidateQueries({
                    queryKey: templateKeys.row(savedId)
                });
            }
            if (target && (savedBlocksDirty || wasCreate)) {
                await queryClient.invalidateQueries({
                    queryKey: templateKeys.doc(target)
                });
            }
            if (!current()) return;
            if (library)
                flushSync(() => {
                    setBlocksDirty(false);
                    setSubjectDirty(false);
                    setPreheaderDirty(false);
                    setNameDirty(false);
                    unregister();
                });
            if (savedId != null) onSavedRef.current?.(savedId, savedRow);
        } catch (err: unknown) {
            if (!current()) return;
            if (axios.isAxiosError(err)) {
                const data = err.response?.data as
                    | {
                          errors?: string[];
                          detail?: string;
                      }
                    | undefined;
                const codes = data?.errors;
                if (codes?.length) {
                    setSaveError(codes.map(describeErrorCode).join('; '));
                } else if (data?.detail) {
                    setSaveError(data.detail);
                } else {
                    setSaveError(`Save failed (${err.response?.status ?? 'unknown'})`);
                }
            } else {
                setSaveError(err instanceof Error ? err.message : 'Save failed');
            }
        } finally {
            if (current()) setSaving(false);
        }
    };
    const unregister = useUnsavedChangesRegistration(targetKey, isDirty, saving);
    const validateQuery = useTemplateValidate(docHash, validateBody, true);
    const editorContext = useMemo(() => {
        const ctx = contextQuery.data?.data;
        return {
            variables: (ctx?.variables ?? []).filter(
                (variable) =>
                    !variable.requires_target_kinds?.length ||
                    targetKind === undefined ||
                    variable.requires_target_kinds.includes(targetKind ?? '')
            ),
            urlSlugs: ctx?.url_slugs ?? [],
            channel,
            target,
            insertTarget: readOnly ? null : insertTarget,
            setInsertTarget,
            canvas: canvasMode,
            showErrors: saveAttempted,
            readOnly,
            validation: contentValidation
        };
    }, [
        contextQuery.data,
        targetKind,
        channel,
        target,
        insertTarget,
        setInsertTarget,
        readOnly,
        canvasMode,
        saveAttempted,
        contentValidation
    ]);
    const metadata = channel !== 'sms' && (
        <details
            className={metadataStyles.settings}
            open={settingsOpen}
            onToggle={(event) => setSettingsOpen(event.currentTarget.open)}
        >
            <summary>
                <strong>Email settings</strong>
                <span className={metadataStyles.settingsSummary}>{subject || 'Add a subject'}</span>
            </summary>
            <fieldset
                disabled={saving || readOnly}
                className={`${metadataStyles.fields} ${library ? metadataStyles.libraryFields : ''} ${canvasStyles.metadataFields}`}
            >
                {library && (
                    <div className={layout.field}>
                        <div className={canvasStyles.metadataFieldHeader}>
                            <MarkLabel htmlFor={nameId}>Name</MarkLabel>
                        </div>
                        <MarkInput
                            compact
                            id={nameId}
                            value={name}
                            maxLength={255}
                            required
                            disabled={saving || readOnly}
                            placeholder="Internal template name"
                            aria-invalid={saveAttempted && !!nameError}
                            aria-describedby={
                                saveAttempted && nameError ? `${nameId}-error` : undefined
                            }
                            // Name is not personalizable; keep variables from editing the last field.
                            onFocus={() => setInsertTarget(null)}
                            onChange={(event) => {
                                setName(event.currentTarget.value);
                                setNameDirty(true);
                            }}
                        />
                        {saveAttempted && nameError && (
                            <p id={`${nameId}-error`} role="alert" className={layout.fieldError}>
                                {nameError}
                            </p>
                        )}
                    </div>
                )}
                <TemplateTextField
                    compact
                    id={subjectId}
                    label="Subject"
                    error={saveAttempted ? subjectError : undefined}
                    value={subject}
                    disabled={saving || readOnly}
                    onChange={(value) => {
                        setSettingsOpen(true);
                        setSubject(value);
                        setSubjectDirty(true);
                    }}
                />
                <TemplateTextField
                    compact
                    id={preheaderId}
                    label={canvasMode ? 'Preview text' : 'Preheader'}
                    error={saveAttempted ? preheaderError : undefined}
                    value={preheader}
                    disabled={saving || readOnly}
                    onChange={(value) => {
                        setSettingsOpen(true);
                        setPreheader(value);
                        setPreheaderDirty(true);
                    }}
                />
            </fieldset>
        </details>
    );
    const validation = (!canvasMode ||
        (saveAttempted && (!validateQuery.data?.data?.valid || validateQuery.error))) && (
        <ValidateBanner
            data={validateQuery.data?.data}
            isLoading={validateQuery.isLoading}
            isStale={validateQuery.isPlaceholderData}
            error={validateQuery.error}
        />
    );
    const content = (
        <fieldset
            ref={contentRef}
            disabled={saving || readOnly}
            style={{
                flex: 1,
                minWidth: 0,
                margin: 0,
                opacity: saving || readOnly ? 0.6 : 1
            }}
            className={layout.fieldset}
        >
            <EditableBlockList
                blocks={blocks}
                onChange={handleBlocksChange}
                channel={channel}
                depth={0}
                variant="root"
                showInsertLabels={canvasMode}
            />
        </fieldset>
    );
    const preview = (
        <PreviewPanel
            ref={previewRef}
            key={targetKey}
            blockDoc={renderDoc}
            channel={channel}
            parentSubject={subject}
            compact={canvasMode}
            syntheticOnly={library}
        />
    );
    const saveFeedback = (
        <>
            {saveError && (
                <div
                    role="alert"
                    style={{
                        backgroundColor: 'var(--mark-danger-surface)',
                        color: 'var(--mark-danger)',
                        marginTop: '1.2rem'
                    }}
                    className={layout.notice}
                >
                    {saveError}
                </div>
            )}
            {!readOnly && localErrors.length > 0 && saveAttempted && (
                <div role="alert" aria-label="Template fields to review">
                    <P noDefaultMargin strong className={canvasStyles.error}>
                        Review these fields before saving
                    </P>
                    <ul
                        style={{
                            margin: '1.2rem 0 0',
                            paddingLeft: '1.6rem',
                            color: 'var(--mark-danger)'
                        }}
                    >
                        {localErrors.map((message, index) => (
                            <li key={`${index}-${message}`}>{message}</li>
                        ))}
                    </ul>
                </div>
            )}
        </>
    );
    const saveAction = !readOnly && (
        <MarkButton
            compact={channel === 'email'}
            variant="primary"
            size="regular"
            type="button"
            disabled={!canSave}
            onClick={handleSave}
            style={{
                marginTop: canvasMode ? 0 : '1.6rem'
            }}
        >
            {saving ? 'Saving…' : 'Save template'}
        </MarkButton>
    );
    return (
        <EditorContext.Provider value={editorContext}>
            {canvasMode ? (
                <TemplateWorkspaceLayout
                    metadata={metadata}
                    validation={validation}
                    content={content}
                    preview={preview}
                    previewOpen={previewOpen}
                    onPreviewChange={(open) => {
                        setPreviewOpen(open);
                        if (open) previewRef.current?.refresh();
                    }}
                    saveFeedback={saveFeedback}
                    saveAction={saveAction}
                    status={
                        readOnly
                            ? 'Read-only template'
                            : saving
                              ? 'Saving template…'
                              : isDirty
                                ? 'Unsaved changes'
                                : effectiveTemplateId == null
                                  ? 'Start building your template'
                                  : 'All changes saved'
                    }
                />
            ) : (
                <>
                    {channel === 'sms' && (
                        <div
                            style={{
                                padding: '0.8rem 1.2rem',
                                backgroundColor: 'var(--mark-selected)',
                                borderRadius: '0.4rem',
                                marginBottom: '1.2rem'
                            }}
                        >
                            <P noDefaultMargin size="small">
                                SMS template — only paragraph blocks are supported.
                            </P>
                        </div>
                    )}
                    {metadata}
                    {validation}
                    <div className={layout.editorLayout}>
                        {content}
                        <div className={layout.stack}>
                            <VariableList interactive={!readOnly} />
                        </div>
                    </div>
                    {preview}
                    {saveFeedback}
                    {saveAction}
                </>
            )}
        </EditorContext.Provider>
    );
};
type TemplateFormat = 'blocks' | 'html';
// picked: a library template copied into this draft. A picked template or a type other
// than the saved one is unsaved work from the start, so Save is enabled and leaving asks.
type TemplateChoice = { format: TemplateFormat; row: TemplateContent | null; picked?: boolean };

const TemplateEditorShell: React.FC<Props> = (props) => {
    const [saved, setSaved] = useState({ id: props.templateId, row: props.markTemplate });
    const [selection, setSelection] = useState<(TemplateChoice & { revision: number }) | null>(
        null
    );
    const [pending, setPending] = useState<TemplateChoice | null>(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const editorRef = useRef<HTMLDivElement>(null);
    const typeId = useId();
    const guard = useContext(UnsavedChangesContext);
    const token = props.target ? JSON.stringify(props.target) : `template-library-${typeId}`;
    const saving = guard.isSaving(token);
    const currentSelection = props.readOnly ? null : selection;
    const row = currentSelection ? currentSelection.row : saved.row;
    const format =
        props.channel === 'sms'
            ? 'blocks'
            : (currentSelection?.format ?? saved.row?.format ?? 'blocks');
    const savedFormat = saved.row?.format ?? 'blocks';
    const apply = (choice: TemplateChoice) => {
        if (props.readOnly || guard.isSaving(token)) return;
        guard.unregister(token);
        // Going back to the saved format and row restores the saved editor, not a dirty draft.
        if (choice.format === savedFormat && choice.row === saved.row) setSelection(null);
        else setSelection((previous) => ({ ...choice, revision: (previous?.revision ?? 0) + 1 }));
        setPending(null);
        setPickerOpen(false);
        // Collapsed sections and closed settings hide their fields; focus the first visible control.
        const firstVisible = (selector: string) =>
            Array.from(editorRef.current?.querySelectorAll<HTMLElement>(selector) ?? []).find(
                (element) => !element.closest('[hidden], details:not([open]) > :not(summary)')
            );
        requestAnimationFrame(() =>
            (
                firstVisible('textarea:not(:disabled)') ??
                firstVisible('input:not(:disabled)') ??
                firstVisible('button:not(:disabled), summary')
            )?.focus()
        );
    };
    const choose = (choice: TemplateChoice) => {
        if (props.readOnly || guard.isSaving(token)) return;
        if (guard.isDirty(token)) setPending(choice);
        else apply(choice);
    };
    const handleSaved = (id: number, savedRow: MarkTemplateRow) => {
        setSaved({ id, row: savedRow });
        setSelection((previous) =>
            previous ? { ...previous, row: savedRow, picked: false } : null
        );
        props.onSaved?.(id, savedRow);
    };
    const editorKey = `${format}-${currentSelection?.revision ?? 0}`;
    const unsavedChoice =
        props.initialDirty === true || currentSelection?.picked === true || format !== savedFormat;
    return (
        <>
            {props.channel === 'email' && !props.readOnly && (
                <>
                    <div className={authoring.toolbar}>
                        {!pickerOpen && (
                            <fieldset className={authoring.formatChoices} disabled={saving}>
                                <legend className={layout.srOnly}>Email format</legend>
                                <span className={authoring.hint} aria-hidden="true">
                                    Format
                                </span>
                                {(['blocks', 'html'] as const).map((choice) => (
                                    <MarkChoiceLabel
                                        compact
                                        key={choice}
                                        className={authoring.formatChoice}
                                        htmlFor={`${typeId}-${choice}`}
                                    >
                                        <MarkChoice
                                            type="radio"
                                            id={`${typeId}-${choice}`}
                                            name={typeId}
                                            value={choice}
                                            checked={format === choice}
                                            disabled={saving}
                                            aria-label={choice === 'html' ? 'HTML' : 'Blocks'}
                                            onChange={() => {
                                                // Keep the saved subject and preview text in either format.
                                                if (choice !== format)
                                                    choose({ format: choice, row: saved.row });
                                            }}
                                        />
                                        {choice === 'html' ? 'HTML' : 'Blocks'}
                                    </MarkChoiceLabel>
                                ))}
                            </fieldset>
                        )}
                        {!props.library && (
                            <MarkButton
                                compact
                                variant="outlineSecondary"
                                disabled={saving}
                                aria-expanded={pickerOpen}
                                onClick={() => setPickerOpen(!pickerOpen)}
                            >
                                {pickerOpen ? 'Back to editor' : 'Choose library template'}
                            </MarkButton>
                        )}
                    </div>
                    {pickerOpen && !props.library && (
                        <TemplateLibraryPicker
                            onPick={(choice) =>
                                choose({
                                    format: choice.format === 'html' ? 'html' : 'blocks',
                                    row: choice,
                                    picked: true
                                })
                            }
                        />
                    )}
                    {currentSelection?.picked && !pickerOpen && (
                        <p role="status" className={authoring.hint}>
                            Copied{row?.name ? ` from “${row.name}”` : ' from the library'}. Review
                            your email and save to keep it in this draft.
                        </p>
                    )}
                </>
            )}
            <div
                ref={editorRef}
                className={authoring.editorBody}
                hidden={pickerOpen && !props.readOnly}
            >
                {format === 'html' ? (
                    <HtmlTemplateEditor
                        key={editorKey}
                        initial={
                            props.library
                                ? { ...row, name: row?.name ?? saved.row?.name ?? '' }
                                : currentSelection?.picked && row
                                  ? {
                                        html: row.html,
                                        subject: row.subject,
                                        preheader: row.preheader
                                    }
                                  : (row ?? undefined)
                        }
                        templateId={saved.id}
                        target={props.target}
                        targetKind={props.targetKind}
                        readOnly={props.readOnly}
                        initialDirty={unsavedChoice}
                        guardToken={token}
                        onSaved={handleSaved}
                    />
                ) : (
                    <BlockTemplateEditorShell
                        {...props}
                        key={editorKey}
                        markTemplate={row}
                        templateId={saved.id}
                        templateLoadState={saved.id != null ? 'existing' : props.templateLoadState}
                        initialDirty={unsavedChoice}
                        guardToken={token}
                        onSaved={handleSaved}
                    />
                )}
            </div>
            {pending && !props.readOnly && (
                <MarkModal
                    aria-label="Discard unsaved changes?"
                    isPending={saving}
                    onClose={() => setPending(null)}
                >
                    <P>Your unsaved template changes will be lost.</P>
                    <MarkConfirmActions
                        confirmLabel="Discard"
                        cancelLabel="Stay"
                        onConfirm={() => apply(pending)}
                        onCancel={() => setPending(null)}
                        disabled={saving}
                        cancelDisabled={saving}
                    />
                </MarkModal>
            )}
        </>
    );
};

export default TemplateEditorShell;
